import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { useFormHandler } from "../../../../../components";
import {
  getIncentiveSettingsStart,
  saveIncentiveSettingsStart,
} from "../../../../../redux/slices/incentives/incentiveSlice";

const useIncentiveSetting = ({
  settingsKey,
  listFieldName, // e.g., "benchSaleSettingDetails"
  initialFormData,
  validationConfig,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { incentiveSettings, loading } = useSelector((state) => state.incentive);
  const { user } = useSelector((state) => state.auth);

  const {
    formData,
    errors,
    handleChange,
    validateAllFields,
    setFormData,
  } = useFormHandler(initialFormData, validationConfig);

  // Fetch settings on mount
  useEffect(() => {
    dispatch(getIncentiveSettingsStart(settingsKey));
  }, [dispatch, settingsKey]);

  // Sync Redux state to local form data
  useEffect(() => {
    const savedSettings = incentiveSettings?.[settingsKey];
    if (savedSettings) {
      setFormData((prev) => ({
        ...prev,
        ...savedSettings.value,
        quarterlyTarget: savedSettings.quarterlyTarget !== undefined && savedSettings.quarterlyTarget !== null ? String(savedSettings.quarterlyTarget) : "",
        yearlyTarget: savedSettings.yearlyTarget !== undefined && savedSettings.yearlyTarget !== null ? String(savedSettings.yearlyTarget) : "",
      }));
    }
  }, [incentiveSettings, settingsKey, setFormData]);

  // Generic Add Item
  const addItem = useCallback(() => {
    const currentList = formData[listFieldName] || [];
    if (currentList.length < 5) {
      // Create a new empty item based on the structure of the first item in initialFormData
      // OR mostly just empty strings, but preserving keys is good.
      // We can infer keys from the initialFormData's list item template
      const templateItem = initialFormData[listFieldName][0]; 
      const newItem = Object.keys(templateItem).reduce((acc, key) => {
          acc[key] = key === 'id' ? uuidv4() : ""; 
          return acc;
      }, {});

      setFormData({
        ...formData,
        [listFieldName]: [...currentList, newItem],
      });
    }
  }, [formData, listFieldName, initialFormData, setFormData]);

  // Generic Remove Item
  const removeItem = useCallback((id) => {
    const currentList = formData[listFieldName] || [];
    if (currentList.length > 1) {
      const updated = currentList.filter((item) => item.id !== id);
      setFormData({
        ...formData,
        [listFieldName]: updated,
      });
    }
  }, [formData, listFieldName, setFormData]);

  // Save Handler
  const saveSettings = useCallback(() => {
    console.log('User object:', user);
    const isValid = validateAllFields();
    if (isValid) {
      // Separate targets from the rest of the value payload
      const { quarterlyTarget, yearlyTarget, ...rest } = formData;
      
      const payload = {
        key: settingsKey,
        uiid: incentiveSettings?.[settingsKey]?.uiid,
        addedBy: user?.data?.uiid,
        quarterlyTarget: quarterlyTarget,
        yearlyTarget: yearlyTarget,
        value: rest, // Everything else goes into 'value' JSON
      };
      
      const callbacks = {
        onSuccess: () => {
          if (onClose) onClose();
        },
        onError: (msg) => console.error(msg),
      };
      
      console.log('Saving incentive settings with payload:', payload);
      dispatch(saveIncentiveSettingsStart({ ...payload, ...callbacks }));
    } else {
      console.log("Validation errors:", errors);
    }
  }, [
    validateAllFields,
    formData,
    settingsKey,
    incentiveSettings,
    dispatch,
    onClose,
  ]);

  return {
    formData,
    errors,
    handleChange,
    loading,
    addItem,
    removeItem,
    saveSettings,
  };
};

export default useIncentiveSetting;
